/* eslint-disable */
import {
  api,
  setAuthToken,
  translateApiError,
  setAuthFailureHandler,
  clearAuthFailureHandler
} from "@/services/api";
import { JWT_REFRESH_INTERVAL } from "@/config/constants";
import { resetState } from "@/store/helpers";
import { createDevice } from "@/services/device";
import * as Sentry from "@sentry/vue";
import { isProxy, toRaw } from 'vue';

import i18n from '@/i18n'
// import { setAuthenticatedUser } from "@/router";

const initState = () => {
  return {
    authenticated: false, // this means a user is authenticated
    userId: null, // id of user who is authenticated, if relevant
    jwtToken: null, // jwt token for authenticated user
    refreshToken: null,
    deviceAuthenticated: false, // this means the device is authenticated
    deviceId: null, // id of authenticated device
    lastAuthorizedAt: null, // Date() when last token was refreshed

    // refreshes are generally triggered by intervals, timeouts, or other conditions. So it
    // is possible that two may end up happening almost at the same time, which would result
    // in an auth failure due to the tokens only being usable once.  This param is an internal
    // guard to prevent the `refresh` action from dispatching if another is already in progress.
    refreshInProgress: false,

    // refresh listener - for use with browser api
    tokenRefreshListener: null,
    configState:false
  };
};

const state = {
  initialized: false, // this actually should only be set to false once when the page loads initially
  // TODO: Need better way to use this
  fcmToken: null,
  zoomSdkConfig: null,
  ...initState()
};

const mutations = {
  reset(state) {
    resetState(state, initState());
    setAuthToken(null);
    localStorage.removeItem("auth:refreshToken");
  },
  authSuccess(state, data) {
    let parsedJwt = null;
    if (!!data.token) {
      parsedJwt = parseJwt(data.token);
    }

    state.initialized = true;
    state.jwtToken = data.token;

    // have an authenticated device
    if (!!parsedJwt && !!parsedJwt.device) {
      state.deviceAuthenticated = true;
      state.deviceId = parsedJwt.device.id;
    } else {
      state.deviceAuthenticated = false;
      state.deviceId = null;
    }

    // have an authenticated user
    if (!!parsedJwt && !!parsedJwt.user) {
      state.authenticated = true;
      state.userId = parsedJwt.user.id;
    } else {
      state.authenticated = false;
      state.userId = null;
    }

    state.lastAuthorizedAt = new Date();
    state.refreshToken = data.refresh;
    setAuthToken(state.jwtToken);
    localStorage.setItem("auth:refreshToken", state.refreshToken);
    // TODO: might need to store jwt token in some cases (device only auth)
  },
  refreshInProgress(state, isInProgress) {
    state.refreshInProgress = isInProgress;
  },
  authFailed(state, err) {
    state.initialized = true;
    state.authenticated = false;
  },
  restoreToken(state) {
    let token = localStorage.getItem("auth:refreshToken");
    if (!!token) {
      state.refreshToken = token;
    }
    // TODO: might need to restore jwt token
  },
  setTokenRefreshListener(state, listener) {
    state.tokenRefreshListener = listener;
  },
  clearToken(state) {
    state.jwtToken = null;
    state.refreshToken = null;
    localStorage.removeItem("auth:refreshToken");
    setAuthToken(null);
  },
  logout(state) {
    state.authenticated = false;
    state.userId = null;
    state.jwtToken = null;
    state.refreshToken = null;
    setAuthToken(null);
    localStorage.removeItem("auth:refreshToken");
    localStorage.removeItem("feedback:sessionBefore24Hour");
    // TODO: might need to remove stored jwt token
  },
  setNotificationToken(state, token) {
    state.fcmToken = token;
  },
  setClientPlatformInfo(state, config) {
    state.zoomSdkConfig = config;
  },
};

const actions = {
  // restore auth state from localstorage or cookies
  initialize({ commit, dispatch, state }) {
    commit("restoreToken");
    return new Promise((resolve, reject) => {
      // if there was no refresh token, then there's nothing to do
      if (!state.refreshToken || state.refreshToken == "undefined") {
        // TODO: might need to restore device JWT
        resolve();
        return;
      }

      // otherwise, use the token and update the user data
      dispatch("refresh")
        .then(() => {
          return dispatch("user/refresh", state.userId, { root: true });
        })
        .then(user => {
          // setAuthenticatedUser(user);
          dispatch("setupTokenRefreshListener");
          resolve();
        })
        .catch((e)=>{
          //GPS-3045?focusedCommentId=12703
          commit("logout");
          reject(e);
        });
    });
  },
  // setup page listener to automatically use refresh token once focus comes back -
  // otherwise user will experience auth failures if they switch away from the tab
  // for too long
  setupTokenRefreshListener({ commit, dispatch, state }) {
    if (!!state.tokenRefreshListener) {
      return;
    }

    const listener = doc => {
      // if doc is no longer hidden, check for last time that was refreshed
      if (!doc.hidden) {
        // has it been long enough that we should refresh the auth token?
        if (
          !state.lastAuthorizedAt ||
          (!!state.lastAuthorizedAt &&
            new Date().getTime() >
              state.lastAuthorizedAt.getTime() + JWT_REFRESH_INTERVAL)
        ) {
          if (!!state.refreshToken) {
            dispatch("refresh");
          }
        }
      }
    };

    // NOTE: it is theoretically possible that another API request may be triggered before
    // the response from `refresh` comes back.  We MAY need to implement new features in
    // the API service that allow buffering API requests until a "refresh" response
    // has completed.
    document.addEventListener("visibilitychange", listener, false);
    commit("setTokenRefreshListener", listener);
  },

  // use a refresh token to authenticate
  refresh({ commit, state, dispatch }) {
    if (state.refreshInProgress) {
      return;
    }

    // get the token from local storage, instead of local state.  It's possible another
    // browser tab has already used the current token
    let token = localStorage.getItem("auth:refreshToken");
    if (!token) {
      return Promise.reject(new Error("no refresh token"));
    }

    commit("refreshInProgress", true);
    // unset the current JWT before requesting new one with the token
    // TODO: MAYBE don't unset JWT, because device ID might need to be passed
    setAuthToken(null);

    // also unset the auth retry handler - if we have an auth failure why trying
    // to use a refresh token, it could lead to an infinite loop
    clearAuthFailureHandler();

    // now try to use the refresh token
    return new Promise((resolve, reject) => {
      api
        .post("/v1/auth/tokens", { token: token })
        .then(res => {
          commit("authSuccess", res.data);
          setSentryUser(state.userId, state.deviceId);

          // automatically use the refresh token at the configured interval
          setTimeout(() => {
            dispatch("refresh").catch(() => {
              dispatch("logout").catch(e => {
                return console.error(i18n.global.t('auth.logoutError'));
              });
            });
          }, JWT_REFRESH_INTERVAL);

          // set the auth failure handler - if a request fails
          // it should try to use a refresh token if available and retry,
          // otherwise fail and logout the user
          setAuthFailureHandler(null, err => {
            return dispatch("handleAuthFailure", err);
          });

          resolve();
        })
        .catch(e => {
          // if we failed to use the refresh token, then
          // we're basically logged out, so clear all
          // internal auth and user state
          commit("authFailed");
          commit("logout");
          commit("user/reset", null, { root: true });
          setSentryUser(null, null);
          reject(e);
        })
        .finally(() => {
          commit("refreshInProgress", false);
        });
    });
  },

  // create and set a device JWT token
  authorizeDevice({ commit , state}) {
    let config = state.zoomSdkConfig;
    if (isProxy(state.zoomSdkConfig)) {
      config = toRaw(state.zoomSdkConfig);
    }
    const device = createDevice(config);
    return new Promise((resolve, reject) => {
      api
        .post("/v1/auth/device", device)
        .then(res => {
          commit("authSuccess", res.data);
          resolve();
        })
        .catch(reject);
    });
  },

  // update user device with specified fields
  updateDevice({ commit, state, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      api
        .patch(`/v1/devices/${state.deviceId}`, payload)
        .then(res => {
          resolve(res.data);
        })
        .catch(reject);
    });
  },

  // basic login with email/pass combo
  login({ commit, state, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      commit("reset");
      commit("user/reset", null, { root: true });

      // TODO: if you log in, it doesn't look like the timeout is being set
      // to user the refresh token if received.  The whole thing should probably be refactored
      // to automatically set the timeout to use the token if present in the authentication
      // response
      let config = state.zoomContext;
      if (isProxy(state.zoomContext)) {
        config = toRaw(state.zoomContext);
      }

      dispatch("authorizeDevice").finally(() => {
        if (!!payload.email) {
          payload.email = payload.email.trim();
        }
        api
          .post("/v1/auth/user", payload)
          .then(res => {
            commit("authSuccess", res.data);
            setSentryUser(state.userId, state.deviceId);
            dispatch("setupTokenRefreshListener");
            dispatch("updateDevice", { notificationToken: state.fcmToken });
            setAuthFailureHandler(null, err => {
              return dispatch("handleAuthFailure", err);
            });
            return dispatch("user/refresh", state.userId, { root: true }).then(
              user => {
                // Check if user is a linguist and prevent login
                if (!!user.linguistProfile) {
                  // Reject with a specific error for linguist restriction
                  return Promise.reject({
                    response: {
                      status: 403,
                      data: { errors: [i18n.global.t("loginErrors.linguistRestriction")] }
                    }
                  });
                }
              }
            );
          })
          .then(resolve)
          .catch(e => {
            commit("reset");
            commit("authFailed");
            setSentryUser(null, null);
            reject(e);
          });
      });
    });
  },
  
  // payload here is an axios response error
  handleAuthFailure({ state, dispatch }, payload) {
    const res = payload.response || {};
    const reqConfig = payload.config || {};

    // if we have an auth failure, check if it's possible to retry
    if (!!res.status && res.status === 401) {
      // if no token, then request just fails
      // TODO: should this also force clean internal auth state?
      if (!!!state.refreshToken) {
        return Promise.reject(payload);
      }

      // use the refresh token and retry the request, but ensure
      // we don't get stuck in a loop if the retry fails... only reset
      // the auth failure handler after the retry has finished
      return dispatch("refresh").then(() => {
        reqConfig.headers.Authorization='Bearer ' + state.jwtToken
        clearAuthFailureHandler();
          return api.request(reqConfig).finally(() => {
            setAuthFailureHandler(null, err => {
              return dispatch("handleAuthFailure", err);
            });
            });
          });
    }

    return Promise.reject(payload);
  },

  // logout, will also remove the current device
  logout({ commit }) {
    return new Promise((resolve, reject) => {
      api
        .get("/v1/auth/logout")
        .then(resolve)
        .catch(e => {
          reject(translateApiError(e, "auth.logoutError"));
        })
        .finally(() => {
          commit("logout");
          commit("user/reset", null, { root: true });
          // commit("organization/reset", null, { root: true });
          commit("session/reset", null, { root: true });
          setSentryUser(null, null) //clear user on logout
        });
    });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};

// thanks internet: https://stackoverflow.com/a/38552302
function parseJwt(token) {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function(c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}

function setSentryUser(id , deviceId){
  if(id && deviceId){
    Sentry.setUser({id , deviceId}) // set userId and deviceId for user identification on Sentry
    return;
  }
  Sentry.setUser({}) //for clearing existing user (in case of logout/authfailure)
}